import { useState, useEffect, useCallback, Fragment } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import apiV2 from '../../utils/apiV2';
import { toast } from 'sonner';
import { apiErrorText } from '../../lib/apiError';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { TxDetailRow, SOURCE_LABEL } from './TxDetailRow';

const fmtMoney = (n, c = 'AED') => `${c} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ', ' + new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const DOT = {
  completed: 'bg-blue-500',
  created: 'bg-gray-300',
  pending: 'bg-blue-300',
  requires_payment_instrument: 'bg-gray-300',
  requires_user_action: 'bg-blue-300',
  failed: 'bg-red-400',
  canceled: 'bg-gray-300',
};
const STATUS_TEXT = {
  created: 'checkout created',
  requires_payment_instrument: 'awaiting payment',
  requires_user_action: 'awaiting action',
};
const PAYOUT_TEXT = { owed: 'payout owed', sent: 'payout sent', manual: 'paid manually', pending: 'payout pending' };
const FEE_TEXT = { owed: '3% fee owed', sent: '3% fee collected', manual: '3% fee settled', pending: 'fee pending', na: '—' };
const TYPE_LABEL = {
  deposit: 'Deposit', full: 'Payment', balance: 'Balance', extras: 'Extras',
  customer_booking: 'Payment', balance_payment: 'Balance', balance_collection: 'Balance',
  customer_ticket_payment: 'Balance', gift_card_purchase: 'Gift card',
  marketing_credits: 'Ad credits', reconciled_charge: 'Card charge',
};

const ProcessorBadge = ({ p, id }) => (
  <span
    data-testid={`tx-processor-badge-${id}`}
    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide ${
      p === 'ziina' ? 'bg-violet-50 text-violet-600' : 'bg-blue-50 text-blue-600'
    }`}
  >
    {p === 'ziina' ? 'Ziina' : 'Stripe'}
  </span>
);

const typeLabel = (tx) => {
  if (tx.type === 'refund') return 'Refund';
  if (tx.type === 'fee_transfer') return 'Fee (3%)';
  if (tx.type === 'payout') return 'Payout';
  return TYPE_LABEL[tx.paymentType] || 'Payment';
};

/**
 * UnifiedLedger — one transactions table across ALL processors. Each row is
 * tagged with the processor it ran on; Ziina rows keep their payout/fee state,
 * mark-paid and refund actions.
 */
export const UnifiedLedger = ({ orgId, ziinaStatus, onSummary, hideWhenEmpty = false }) => {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [expanded, setExpanded] = useState(null);
  const [refundTx, setRefundTx] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);

  const load = useCallback(async () => {
    setState('loading');
    try {
      const r = await apiV2.get(`/organizations/${orgId}/payments/transactions`);
      setData(r.data);
      onSummary?.(r.data.summary);
      setState('ok');
    } catch {
      setState('error');
    }
  }, [orgId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const markPaid = async (tx) => {
    try {
      await apiV2.post(`/organizations/${orgId}/ziina/payouts/${tx.id}/mark-paid`);
      toast.success('Payout marked as sent');
      load();
    } catch (e) { toast.error(apiErrorText(e, 'Could not update payout')); }
  };

  const openRefund = (tx) => {
    setRefundTx(tx);
    setRefundAmount(String((tx.amount - tx.refundedAmount).toFixed(2)));
    setRefundReason('');
  };

  const handleRefund = async () => {
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setRefunding(true);
    try {
      await apiV2.post(`/organizations/${orgId}/ziina/refund`, {
        bookingId: refundTx.bookingId, amount: amt, reason: refundReason.trim() || null,
      });
      toast.success(`Refund of ${fmtMoney(amt, refundTx.currency)} sent to Ziina`);
      setRefundTx(null);
      load();
    } catch (e) {
      toast.error(apiErrorText(e, 'Refund failed'));
    } finally { setRefunding(false); }
  };

  const transactions = data?.transactions || [];
  const hasZiina = transactions.some((t) => t.processor === 'ziina') || !!ziinaStatus?.connected;
  if (hideWhenEmpty && state === 'ok' && transactions.length === 0) return null;
  const inputCls = 'w-full h-9 px-3 text-[13px] border border-gray-200 rounded-lg bg-white text-gray-900 placeholder:text-gray-300 focus:outline-none focus:border-gray-400 transition-colors';

  const detailPairs = (tx) => tx.processor === 'ziina'
    ? [
        ['Processor', 'Ziina'],
        ['Service', tx.serviceName],
        ['Sales channel', tx.salesChannel],
        ['Collected via', SOURCE_LABEL[tx.source] || tx.source],
        ['Customer email', tx.customerEmail],
        ['Fee breakdown', tx.feeBreakdown],
        ['Ziina processing fee', tx.ziinaFee ? fmtMoney(tx.ziinaFee, tx.currency) : null],
        ['Platform fee', tx.platformFee ? fmtMoney(tx.platformFee, tx.currency) : null],
        ['Net payout', tx.netPayout != null ? fmtMoney(tx.netPayout, tx.currency) : null],
        [tx.collectionModel === 'merchant_collect' ? 'Fee status' : 'Payout status',
         tx.type === 'payment'
           ? (tx.collectionModel === 'merchant_collect'
               ? (FEE_TEXT[tx.payoutStatus] || tx.payoutStatus)
               : (PAYOUT_TEXT[tx.payoutStatus] || tx.payoutStatus))
           : null],
        ['Payment reference', tx.paymentIntentId],
        ['Note', tx.description],
      ]
    : [
        ['Processor', 'Stripe'],
        ['Service', tx.serviceName],
        ['Sales channel', tx.salesChannel],
        ['Collected via', tx.source],
        ['Customer email', tx.customerEmail],
        ['Booking date', tx.bookingDate],
        ['Guests', tx.guestCount],
        ['Fee breakdown', tx.feeBreakdown],
        ['Platform fee', tx.applicationFee ? fmtMoney(tx.applicationFee, tx.currency) : null],
        ['Payment reference', tx.paymentIntentId || tx.sessionId],
        ['Note', tx.description],
      ];

  return (
    <div className="card-soft p-6" data-testid="unified-transactions-table">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm text-gray-900 font-medium">Transactions</h4>
          <p className="text-[11px] text-gray-400 mt-0.5">All processors, one ledger — tap a row for the full booking context</p>
        </div>
        <button onClick={load} className="text-gray-300 hover:text-gray-500 transition-colors" data-testid="unified-refresh-tx-btn" aria-label="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {state === 'loading' && !data ? (
        <div className="py-8 flex items-center justify-center gap-2 text-gray-400 border-t border-gray-100" data-testid="unified-tx-loading">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-xs">Loading your ledger…</span>
        </div>
      ) : state === 'error' && !data ? (
        <div className="py-8 text-center border-t border-gray-100" data-testid="unified-tx-error">
          <p className="text-xs text-gray-400">Couldn't load transactions.</p>
          <button onClick={load} className="mt-2 text-xs text-gray-600 underline underline-offset-2 hover:text-gray-900 transition-colors" data-testid="unified-tx-retry-btn">
            Try again
          </button>
        </div>
      ) : transactions.length === 0 ? (
        <p className="text-xs text-gray-400 py-8 text-center border-t border-gray-100">
          No transactions yet. They'll appear the moment a customer pays — whichever processor they pay on.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full min-w-[680px]">
            <thead>
              <tr className="text-left border-b border-gray-200">
                {['Date', 'Booking', 'Customer', 'Type', 'Processor', 'Amount', 'Status', ''].map((h, i) => (
                  <th key={i} className={`py-2 px-2 text-[11px] uppercase tracking-wider text-gray-400 font-normal ${h === 'Amount' ? 'text-right' : ''}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <Fragment key={`${tx.processor}-${tx.id}`}>
                  <tr
                    className="border-b border-gray-100 cursor-pointer hover:bg-gray-50/60 transition-colors"
                    data-testid={`unified-tx-row-${tx.id}`}
                    onClick={() => setExpanded(expanded === tx.id ? null : tx.id)}
                  >
                    <td className="py-2.5 px-2 text-[13px] text-gray-500 whitespace-nowrap">{fmtDate(tx.createdAt)}</td>
                    <td className="py-2.5 px-2 text-[13px] text-gray-900">{tx.confirmationCode || '—'}</td>
                    <td className="py-2.5 px-2 text-[13px] text-gray-600 max-w-[130px] truncate">{tx.customerName || '—'}</td>
                    <td className="py-2.5 px-2 text-[13px] text-gray-500" data-testid={`unified-tx-type-${tx.id}`}>{typeLabel(tx)}</td>
                    <td className="py-2.5 px-2"><ProcessorBadge p={tx.processor} id={tx.id} /></td>
                    <td className={`py-2.5 px-2 text-[13px] text-right tabular-nums whitespace-nowrap ${tx.type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                      {tx.type === 'refund' ? '−' : ''}{fmtMoney(tx.amount, tx.currency)}
                      {tx.test && <span className="ml-1.5 text-[10px] text-gray-300">test</span>}
                    </td>
                    <td className="py-2.5 px-2">
                      <span className="flex items-center gap-1.5 text-[13px] text-gray-600 whitespace-nowrap">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DOT[tx.status] || 'bg-gray-300'}`} />
                        {STATUS_TEXT[tx.status] || (tx.status || '').replace(/_/g, ' ')}
                      </span>
                      {tx.processor === 'ziina' && tx.type === 'payment' && tx.netPayout != null && (
                        <span className="block text-[11px] text-gray-400 mt-0.5 pl-3">
                          net {fmtMoney(tx.netPayout, tx.currency)} · {tx.collectionModel === 'merchant_collect'
                            ? (FEE_TEXT[tx.payoutStatus] || tx.payoutStatus)
                            : (PAYOUT_TEXT[tx.payoutStatus] || tx.payoutStatus)}
                          {tx.payoutStatus === 'owed' && (
                            <button
                              data-testid={`ziina-mark-paid-btn-${tx.id}`}
                              onClick={(e) => { e.stopPropagation(); markPaid(tx); }}
                              className="ml-1.5 text-blue-600 hover:text-blue-700 underline underline-offset-2 transition-colors"
                            >
                              {tx.collectionModel === 'merchant_collect' ? 'Mark settled' : 'Mark paid'}
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      {tx.processor === 'ziina' && tx.refundable && (
                        <button
                          data-testid={`ziina-refund-btn-${tx.id}`}
                          onClick={(e) => { e.stopPropagation(); openRefund(tx); }}
                          className="text-[12px] text-blue-600 hover:text-blue-700 underline underline-offset-2 transition-colors"
                        >
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                  {expanded === tx.id && (
                    <TxDetailRow colSpan={8} testId={`unified-tx-detail-${tx.id}`} pairs={detailPairs(tx)} />
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasZiina && (
        <p className="text-[11px] text-gray-300 mt-3">
          Ziina refunds available for {ziinaStatus?.refundWindowDays ?? 14} days after payment · Platform fee {ziinaStatus?.feePercent ?? 3}%
          {ziinaStatus?.oauthConnected ? ' auto-collected from your wallet' : ' · Payouts net of Ziina processing fees'}
        </p>
      )}

      {/* Refund dialog (Ziina) */}
      <Dialog open={!!refundTx} onOpenChange={(o) => !o && setRefundTx(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="text-sm font-medium text-gray-900">Refund via Ziina</DialogTitle>
          {refundTx && (
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 text-[13px]">
                <div className="flex justify-between px-3 py-2"><span className="text-gray-400">Booking</span><span className="text-gray-900">{refundTx.confirmationCode}</span></div>
                <div className="flex justify-between px-3 py-2"><span className="text-gray-400">Customer</span><span className="text-gray-700">{refundTx.customerName || '—'}</span></div>
                <div className="flex justify-between px-3 py-2"><span className="text-gray-400">Paid</span><span className="text-gray-900 tabular-nums">{fmtMoney(refundTx.amount, refundTx.currency)}</span></div>
                {refundTx.refundedAmount > 0 && (
                  <div className="flex justify-between px-3 py-2"><span className="text-gray-400">Already refunded</span><span className="text-gray-700 tabular-nums">{fmtMoney(refundTx.refundedAmount, refundTx.currency)}</span></div>
                )}
                <div className="flex justify-between px-3 py-2"><span className="text-gray-400">Window closes</span><span className="text-gray-700">{fmtDate(refundTx.refundableUntil)}</span></div>
              </div>
              <div>
                <label className="text-xs text-gray-500">Refund amount ({refundTx.currency})</label>
                <input
                  data-testid="ziina-refund-amount-input"
                  type="number"
                  step="0.01"
                  min="0.01"
                  max={(refundTx.amount - refundTx.refundedAmount).toFixed(2)}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  className={`${inputCls} mt-1`}
                />
              </div>
              <div>
                <label className="text-xs text-gray-500">Reason (optional)</label>
                <input
                  data-testid="ziina-refund-reason-input"
                  placeholder="e.g. weather cancellation"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  className={`${inputCls} mt-1`}
                />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setRefundTx(null)} className="flex-1 h-9 rounded-lg border border-gray-200 text-[13px] text-gray-600 hover:bg-gray-50 transition-colors">Cancel</button>
                <button
                  data-testid="ziina-refund-confirm-btn"
                  onClick={handleRefund}
                  disabled={refunding}
                  className="flex-1 h-9 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-[13px] font-medium transition-colors flex items-center justify-center disabled:opacity-60"
                >
                  {refunding ? <Loader2 className="w-4 h-4 animate-spin" /> : `Refund ${fmtMoney(parseFloat(refundAmount) || 0, refundTx.currency)}`}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedLedger;
